/**
 * Gift certificate form
 *
 * Collects the recipient details for a gift certificate, adds it to the cart, and then offers the
 * buyer the choice of adding another gift or continuing to checkout. Several certificates can be
 * bought in one order, each as its own line item carrying its own recipient, message and date.
 *
 * The cart drawer is deliberately not opened on add. The theme opens it on the "cart:change" event
 * when the base event is "variant:add", so this dispatches "cart:refresh" instead, which updates the
 * cart count, dot and drawer contents silently and leaves the success panel in view.
 */
(() => {
  if (window.customElements.get('gift-certificate-form')) {
    return;
  }

  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const toIsoDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
  };

  /**
   * Formats "2026-12-25" as "December 25, 2026". Built from the parts rather than parsed as a date,
   * because parsing an ISO date string yields UTC midnight and can display the previous day.
   */
  const toReadableDate = (isoDate) => {
    const [year, month, day] = isoDate.split('-').map(Number);

    if (!year || !month || !day) {
      return isoDate;
    }

    return `${MONTHS[month - 1]} ${day}, ${year}`;
  };

  class GiftCertificateForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[action*="/cart/add"]');

      if (!this.form) {
        return;
      }

      this.dateInput = this.querySelector('[data-gift-date]');
      this.fieldsPanel = this.querySelector('[data-gift-fields]');
      this.successPanel = this.querySelector('[data-gift-success]');
      this.addedList = this.querySelector('[data-gift-added-list]');
      this.errorBanner = this.querySelector('[data-gift-error]');
      this.submitButton = this.querySelector('[data-gift-submit]');
      this.addAnotherButton = this.querySelector('[data-gift-add-another]');

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.counterUpdaters = [];

      this.setDateBounds();
      this.setUpCounters(signal);

      this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      this.addAnotherButton?.addEventListener('click', () => this.onAddAnother(), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * The earliest date comes from Liquid so it is calculated in the shop's timezone rather than the
     * buyer's. It is reset on connect in case the page was served from a cache and has gone stale.
     */
    setDateBounds() {
      if (!this.dateInput) {
        return;
      }

      const rendered = this.dateInput.dataset.minDate;
      const today = toIsoDate(new Date());
      const min = rendered && rendered > today ? rendered : today;

      const [year, month, day] = min.split('-').map(Number);
      const latest = new Date(year + 1, month - 1, day);

      this.dateInput.min = min;
      this.dateInput.max = toIsoDate(latest);
    }

    setUpCounters(signal) {
      this.querySelectorAll('[data-gift-counter-for]').forEach((counter) => {
        const field = this.querySelector(`#${counter.dataset.giftCounterFor}`);

        if (!field) {
          return;
        }

        const update = () => {
          counter.textContent = counter.dataset.template
            .replace('[count]', field.value.length)
            .replace('[max]', field.maxLength);
        };

        field.addEventListener('input', update, { signal });
        this.counterUpdaters.push(update);
        update();
      });
    }

    updateCounters() {
      this.counterUpdaters.forEach((update) => update());
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.submitButton?.getAttribute('aria-busy') === 'true') {
        return;
      }

      this.hideError();
      this.setDateBounds();

      if (!this.form.reportValidity()) {
        return;
      }

      const formData = new FormData(this.form);
      const isoDate = this.dateInput?.value;

      if (isoDate) {
        formData.set('properties[Delivery date]', toReadableDate(isoDate));
        formData.set('properties[_Delivery date]', isoDate);
      }

      this.setBusy(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        const result = await response.json();

        if (!response.ok) {
          this.showError(result.description || result.message);
          return;
        }

        this.recordAddedGift(formData);
        this.showSuccess();

        // Updates the cart count, dot and drawer contents without opening the drawer.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        this.showError(this.dataset.genericError);
      } finally {
        this.setBusy(false);
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    onAddAnother() {
      this.form.reset();
      this.setDateBounds();
      this.updateCounters();

      this.successPanel.hidden = true;
      this.fieldsPanel.hidden = false;

      const firstField = this.form.querySelector('input:not([type="hidden"]), textarea');
      firstField?.focus();
    }

    /**
     * Keeps a running list of what has been added, so a buyer part way through a multi gift order can
     * see who they have already covered.
     */
    recordAddedGift(formData) {
      if (!this.addedList) {
        return;
      }

      const item = document.createElement('li');

      item.textContent = this.dataset.addedTemplate
        .replace('[recipient]', formData.get('properties[Recipient name]') || '')
        .replace('[date]', formData.get('properties[Delivery date]') || '');

      this.addedList.append(item);
    }

    showSuccess() {
      this.fieldsPanel.hidden = true;
      this.successPanel.hidden = false;
      this.successPanel.focus();
    }

    showError(message) {
      if (!this.errorBanner) {
        return;
      }

      this.errorBanner.textContent = message || this.dataset.genericError;
      this.errorBanner.hidden = false;
      this.errorBanner.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    hideError() {
      if (this.errorBanner) {
        this.errorBanner.hidden = true;
      }
    }

    setBusy(isBusy) {
      if (!this.submitButton) {
        return;
      }

      if (isBusy) {
        this.submitButton.setAttribute('aria-busy', 'true');
      } else {
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }

  window.customElements.define('gift-certificate-form', GiftCertificateForm);
})();
